import { ApiError, authHeaders } from '@/api/client'
import type { Upload, UploadVisibility } from '@/api/types'

export type UploadFields = { title?: string; department?: string | null; visibility: UploadVisibility }

/** POST /api/uploads with progress events (fetch can't report upload progress). */
export async function uploadFile(
  file: File,
  fields: UploadFields,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<Upload> {
  const headers = await authHeaders()
  const form = new FormData()
  form.append('file', file)
  if (fields.title) form.append('title', fields.title)
  if (fields.department) form.append('department', fields.department)
  form.append('visibility', fields.visibility)

  return new Promise<Upload>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/uploads')
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    xhr.onload = () => {
      let body: unknown = null
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as Upload)
      else {
        const detail = (body as { detail?: unknown } | null)?.detail
        reject(new ApiError(xhr.status, typeof detail === 'string' ? detail : `Upload failed (HTTP ${xhr.status})`))
      }
    }
    xhr.onerror = () => reject(new ApiError(0, 'The upload was interrupted. Check your connection and try again.'))
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(form)
  })
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
